import type { ComponentContentSchema } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup } from '@vibesites/runtime/react';

// ============================================================================
// FAQ
//
// A static accordion of the questions we hear most, built on native
// <details>/<summary> so it works without JavaScript and stays accessible.
//
// Each FAQ is a **slot group** with two related fields — `question` and
// `answer` — so the editor edits a question and its answer together as one
// unit. The list is capped at 20 items (faq.item1 … faq.item20); items are
// authored per-site (no property-field binding beyond the embedded field
// tokens), mirroring the source FAQ page. Empty items simply don't render.
// ============================================================================

// Ordered list of every FAQ slot-group path. Kept in sync with the
// `slotGroups` literal below; the cap of 20 lives here.
const FAQ_ITEM_KEYS = [
  'faq.item1',
  'faq.item2',
  'faq.item3',
  'faq.item4',
  'faq.item5',
  'faq.item6',
  'faq.item7',
  'faq.item8',
  'faq.item9',
  'faq.item10',
  'faq.item11',
  'faq.item12',
  'faq.item13',
  'faq.item14',
  'faq.item15',
  'faq.item16',
  'faq.item17',
  'faq.item18',
  'faq.item19',
  'faq.item20',
] as const;

// A FAQ renders only when its question has real (non-markup) content, so the
// empty tail items (16–20) stay hidden until an author fills them in.
function hasContent(html: string | null | undefined): boolean {
  if (!html) return false;
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim() !== '';
}

export function FAQ() {
  const defs = contentSchema.slotGroups!;

  // Fixed-length map over a constant key list — hook order is stable.
  const items: Record<string, Record<string, string | null>> = {};
  for (const key of FAQ_ITEM_KEYS) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    items[key] = useSlotGroup(key, defs[key]);
  }

  return (
    <section
      className="pt-4 md:pt-8 pb-10 md:pb-16 bg-[var(--vibe-color-background)]"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* No section title — hero already shows “Frequently Asked Questions”
            (overline). Duda’s FAQ list starts directly under the hero. */}
        <div className="faq-list">
          {hasContent(items['faq.item1'].question) && (
            <EditableSlotGroup groupPath="faq.item1" definition={defs['faq.item1']} className="faq-item">
              <details className="faq-details">
                <summary className="faq-question">
                  <span className="faq-question-text" dangerouslySetInnerHTML={{ __html: items['faq.item1'].question || '' }} />
                </summary>
                <div className="faq-answer" dangerouslySetInnerHTML={{ __html: items['faq.item1'].answer || '' }} />
              </details>
            </EditableSlotGroup>
          )}

          {hasContent(items['faq.item2'].question) && (
            <EditableSlotGroup groupPath="faq.item2" definition={defs['faq.item2']} className="faq-item">
              <details className="faq-details">
                <summary className="faq-question">
                  <span className="faq-question-text" dangerouslySetInnerHTML={{ __html: items['faq.item2'].question || '' }} />
                </summary>
                <div className="faq-answer" dangerouslySetInnerHTML={{ __html: items['faq.item2'].answer || '' }} />
              </details>
            </EditableSlotGroup>
          )}

          {hasContent(items['faq.item3'].question) && (
            <EditableSlotGroup groupPath="faq.item3" definition={defs['faq.item3']} className="faq-item">
              <details className="faq-details">
                <summary className="faq-question">
                  <span className="faq-question-text" dangerouslySetInnerHTML={{ __html: items['faq.item3'].question || '' }} />
                </summary>
                <div className="faq-answer" dangerouslySetInnerHTML={{ __html: items['faq.item3'].answer || '' }} />
              </details>
            </EditableSlotGroup>
          )}

          {hasContent(items['faq.item4'].question) && (
            <EditableSlotGroup groupPath="faq.item4" definition={defs['faq.item4']} className="faq-item">
              <details className="faq-details">
                <summary className="faq-question">
                  <span className="faq-question-text" dangerouslySetInnerHTML={{ __html: items['faq.item4'].question || '' }} />
                </summary>
                <div className="faq-answer" dangerouslySetInnerHTML={{ __html: items['faq.item4'].answer || '' }} />
              </details>
            </EditableSlotGroup>
          )}

          {hasContent(items['faq.item5'].question) && (
            <EditableSlotGroup groupPath="faq.item5" definition={defs['faq.item5']} className="faq-item">
              <details className="faq-details">
                <summary className="faq-question">
                  <span className="faq-question-text" dangerouslySetInnerHTML={{ __html: items['faq.item5'].question || '' }} />
                </summary>
                <div className="faq-answer" dangerouslySetInnerHTML={{ __html: items['faq.item5'].answer || '' }} />
              </details>
            </EditableSlotGroup>
          )}

          {hasContent(items['faq.item6'].question) && (
            <EditableSlotGroup groupPath="faq.item6" definition={defs['faq.item6']} className="faq-item">
              <details className="faq-details">
                <summary className="faq-question">
                  <span className="faq-question-text" dangerouslySetInnerHTML={{ __html: items['faq.item6'].question || '' }} />
                </summary>
                <div className="faq-answer" dangerouslySetInnerHTML={{ __html: items['faq.item6'].answer || '' }} />
              </details>
            </EditableSlotGroup>
          )}

          {hasContent(items['faq.item7'].question) && (
            <EditableSlotGroup groupPath="faq.item7" definition={defs['faq.item7']} className="faq-item">
              <details className="faq-details">
                <summary className="faq-question">
                  <span className="faq-question-text" dangerouslySetInnerHTML={{ __html: items['faq.item7'].question || '' }} />
                </summary>
                <div className="faq-answer" dangerouslySetInnerHTML={{ __html: items['faq.item7'].answer || '' }} />
              </details>
            </EditableSlotGroup>
          )}

          {hasContent(items['faq.item8'].question) && (
            <EditableSlotGroup groupPath="faq.item8" definition={defs['faq.item8']} className="faq-item">
              <details className="faq-details">
                <summary className="faq-question">
                  <span className="faq-question-text" dangerouslySetInnerHTML={{ __html: items['faq.item8'].question || '' }} />
                </summary>
                <div className="faq-answer" dangerouslySetInnerHTML={{ __html: items['faq.item8'].answer || '' }} />
              </details>
            </EditableSlotGroup>
          )}

          {hasContent(items['faq.item9'].question) && (
            <EditableSlotGroup groupPath="faq.item9" definition={defs['faq.item9']} className="faq-item">
              <details className="faq-details">
                <summary className="faq-question">
                  <span className="faq-question-text" dangerouslySetInnerHTML={{ __html: items['faq.item9'].question || '' }} />
                </summary>
                <div className="faq-answer" dangerouslySetInnerHTML={{ __html: items['faq.item9'].answer || '' }} />
              </details>
            </EditableSlotGroup>
          )}

          {hasContent(items['faq.item10'].question) && (
            <EditableSlotGroup groupPath="faq.item10" definition={defs['faq.item10']} className="faq-item">
              <details className="faq-details">
                <summary className="faq-question">
                  <span className="faq-question-text" dangerouslySetInnerHTML={{ __html: items['faq.item10'].question || '' }} />
                </summary>
                <div className="faq-answer" dangerouslySetInnerHTML={{ __html: items['faq.item10'].answer || '' }} />
              </details>
            </EditableSlotGroup>
          )}

          {hasContent(items['faq.item11'].question) && (
            <EditableSlotGroup groupPath="faq.item11" definition={defs['faq.item11']} className="faq-item">
              <details className="faq-details">
                <summary className="faq-question">
                  <span className="faq-question-text" dangerouslySetInnerHTML={{ __html: items['faq.item11'].question || '' }} />
                </summary>
                <div className="faq-answer" dangerouslySetInnerHTML={{ __html: items['faq.item11'].answer || '' }} />
              </details>
            </EditableSlotGroup>
          )}

          {hasContent(items['faq.item12'].question) && (
            <EditableSlotGroup groupPath="faq.item12" definition={defs['faq.item12']} className="faq-item">
              <details className="faq-details">
                <summary className="faq-question">
                  <span className="faq-question-text" dangerouslySetInnerHTML={{ __html: items['faq.item12'].question || '' }} />
                </summary>
                <div className="faq-answer" dangerouslySetInnerHTML={{ __html: items['faq.item12'].answer || '' }} />
              </details>
            </EditableSlotGroup>
          )}

          {hasContent(items['faq.item13'].question) && (
            <EditableSlotGroup groupPath="faq.item13" definition={defs['faq.item13']} className="faq-item">
              <details className="faq-details">
                <summary className="faq-question">
                  <span className="faq-question-text" dangerouslySetInnerHTML={{ __html: items['faq.item13'].question || '' }} />
                </summary>
                <div className="faq-answer" dangerouslySetInnerHTML={{ __html: items['faq.item13'].answer || '' }} />
              </details>
            </EditableSlotGroup>
          )}

          {hasContent(items['faq.item14'].question) && (
            <EditableSlotGroup groupPath="faq.item14" definition={defs['faq.item14']} className="faq-item">
              <details className="faq-details">
                <summary className="faq-question">
                  <span className="faq-question-text" dangerouslySetInnerHTML={{ __html: items['faq.item14'].question || '' }} />
                </summary>
                <div className="faq-answer" dangerouslySetInnerHTML={{ __html: items['faq.item14'].answer || '' }} />
              </details>
            </EditableSlotGroup>
          )}

          {hasContent(items['faq.item15'].question) && (
            <EditableSlotGroup groupPath="faq.item15" definition={defs['faq.item15']} className="faq-item">
              <details className="faq-details">
                <summary className="faq-question">
                  <span className="faq-question-text" dangerouslySetInnerHTML={{ __html: items['faq.item15'].question || '' }} />
                </summary>
                <div className="faq-answer" dangerouslySetInnerHTML={{ __html: items['faq.item15'].answer || '' }} />
              </details>
            </EditableSlotGroup>
          )}

          {hasContent(items['faq.item16'].question) && (
            <EditableSlotGroup groupPath="faq.item16" definition={defs['faq.item16']} className="faq-item">
              <details className="faq-details">
                <summary className="faq-question">
                  <span className="faq-question-text" dangerouslySetInnerHTML={{ __html: items['faq.item16'].question || '' }} />
                </summary>
                <div className="faq-answer" dangerouslySetInnerHTML={{ __html: items['faq.item16'].answer || '' }} />
              </details>
            </EditableSlotGroup>
          )}

          {hasContent(items['faq.item17'].question) && (
            <EditableSlotGroup groupPath="faq.item17" definition={defs['faq.item17']} className="faq-item">
              <details className="faq-details">
                <summary className="faq-question">
                  <span className="faq-question-text" dangerouslySetInnerHTML={{ __html: items['faq.item17'].question || '' }} />
                </summary>
                <div className="faq-answer" dangerouslySetInnerHTML={{ __html: items['faq.item17'].answer || '' }} />
              </details>
            </EditableSlotGroup>
          )}

          {hasContent(items['faq.item18'].question) && (
            <EditableSlotGroup groupPath="faq.item18" definition={defs['faq.item18']} className="faq-item">
              <details className="faq-details">
                <summary className="faq-question">
                  <span className="faq-question-text" dangerouslySetInnerHTML={{ __html: items['faq.item18'].question || '' }} />
                </summary>
                <div className="faq-answer" dangerouslySetInnerHTML={{ __html: items['faq.item18'].answer || '' }} />
              </details>
            </EditableSlotGroup>
          )}

          {hasContent(items['faq.item19'].question) && (
            <EditableSlotGroup groupPath="faq.item19" definition={defs['faq.item19']} className="faq-item">
              <details className="faq-details">
                <summary className="faq-question">
                  <span className="faq-question-text" dangerouslySetInnerHTML={{ __html: items['faq.item19'].question || '' }} />
                </summary>
                <div className="faq-answer" dangerouslySetInnerHTML={{ __html: items['faq.item19'].answer || '' }} />
              </details>
            </EditableSlotGroup>
          )}

          {hasContent(items['faq.item20'].question) && (
            <EditableSlotGroup groupPath="faq.item20" definition={defs['faq.item20']} className="faq-item">
              <details className="faq-details">
                <summary className="faq-question">
                  <span className="faq-question-text" dangerouslySetInnerHTML={{ __html: items['faq.item20'].question || '' }} />
                </summary>
                <div className="faq-answer" dangerouslySetInnerHTML={{ __html: items['faq.item20'].answer || '' }} />
              </details>
            </EditableSlotGroup>
          )}
        </div>
      </div>

      <style>{`
        /* Each FAQ is a self-contained, bordered card with breathing room
           between it and its neighbors so the sections read as distinct.
           The card is the EditableSlotGroup wrapper; the <details> lives
           inside it. */
        .faq-list {
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
        }

        .faq-item {
          background: var(--vibe-color-surface, #f4f4f5);
          border: 1px solid var(--vibe-color-border, #e5e5e7);
          border-radius: 8px;
          overflow: hidden;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .faq-item:hover {
          border-color: color-mix(in srgb, var(--vibe-color-primary) 45%, var(--vibe-color-border, #e5e5e7));
        }
        .faq-item:has(> .faq-details[open]) {
          border-color: var(--vibe-color-primary);
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
        }

        .faq-details {
          border: 0;
        }

        .faq-question {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          cursor: pointer;
          list-style: none;
          padding: 1.15rem 1.4rem;
          font-family: var(--vibe-font-heading);
          font-size: 1.05rem;
          font-weight: 700;
          letter-spacing: 0.01em;
          color: var(--vibe-color-text);
          transition: color 0.2s;
        }
        .faq-question:hover {
          color: var(--vibe-color-primary);
        }
        .faq-question-text {
          flex: 1 1 auto;
        }
        /* Hide the native disclosure marker across browsers */
        .faq-question::-webkit-details-marker {
          display: none;
        }
        .faq-question::marker {
          content: "";
        }
        /* Circular chevron toggle on the right; rotates 180° when open. */
        .faq-question::after {
          content: "";
          flex: 0 0 auto;
          width: 1.9rem;
          height: 1.9rem;
          border-radius: 9999px;
          background-color: var(--vibe-color-text);
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: center;
          background-size: 0.95rem;
          transition: transform 0.25s ease, background-color 0.2s ease;
        }
        .faq-details[open] .faq-question::after {
          transform: rotate(180deg);
          background-color: var(--vibe-color-primary);
        }

        .faq-answer {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.65;
          color: var(--vibe-color-text-muted);
          padding: 0 1.4rem 1.3rem;
        }
        .faq-answer p {
          margin: 0 0 0.75rem 0;
        }
        .faq-answer p:last-child {
          margin-bottom: 0;
        }
        .faq-answer a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }
      `}</style>
    </section>
  );
}

// Shared shape for every FAQ item slot group: a question paired with its
// answer, edited together as one unit.
export const contentSchema: ComponentContentSchema = {
  slot: 'FAQ',
  displayName: 'FAQ',
  tokens: {
    'faq.title': {
      label: 'Section Title',
      type: 'richtext',
      description: 'Heading shown above the list of frequently asked questions.',
      defaultValue: '<p>Frequently Asked Questions</p>',
    },
  },
  slotGroups: {
    'faq.item1': {
      label: 'FAQ 1',
      fields: {
        question: {
          label: 'Question',
          type: 'richtext',
          defaultValue:
            'What floor plans are available at <span data-type="field-token" data-field="property_name"></span>?',
        },
        answer: {
          label: 'Answer',
          type: 'richtext',
          defaultValue:
            '<p><span data-type="field-token" data-field="property_name"></span> offers <span data-type="field-token" data-field="property_bedrooms_range"></span> bedroom apartments. Contact the leasing team for availability, details, and pricing information.</p>',
        },
      },
    },
    'faq.item2': {
      label: 'FAQ 2',
      fields: {
        question: {
          label: 'Question',
          type: 'richtext',
          defaultValue: 'What amenities are included in the apartments and community?',
        },
        answer: {
          label: 'Answer',
          type: 'richtext',
          // Pulls the property's own amenities blurb when present so the answer
          // stays accurate per community; the copy below is the fallback preview.
          defaultField: 'property_amenities_blurb',
          defaultValue:
            '<p>Community features include two sparkling pools with a jacuzzi and spa, a fitness center, and a clubhouse lounge, plus a BBQ area, parcel lockers, and parking options. Homes feature stainless steel appliances, quartz countertops, in-unit washer and dryer, private patios, walk-in closets, and air conditioning.</p>',
        },
      },
    },
    'faq.item3': {
      label: 'FAQ 3',
      fields: {
        question: {
          label: 'Question',
          type: 'richtext',
          defaultValue: 'Is this property pet-friendly?',
        },
        answer: {
          label: 'Answer',
          type: 'richtext',
          defaultValue: '<p>Yes, we are a pet-friendly community.</p>',
        },
      },
    },
    'faq.item4': {
      label: 'FAQ 4',
      fields: {
        question: {
          label: 'Question',
          type: 'richtext',
          defaultValue: 'What is the pet policy and fees?',
        },
        answer: {
          label: 'Answer',
          type: 'richtext',
          defaultValue:
            '<p>A maximum of 2 pets per apartment is allowed. The pet fee is $300 per pet (non-refundable), with monthly pet rent of $35 per pet. Dogs are limited to 75 lbs. Breed restrictions apply, including Pit Bulls, Rottweilers, Huskies, and others. Service and emotional support animals are exempt.</p>',
        },
      },
    },
    'faq.item5': {
      label: 'FAQ 5',
      fields: {
        question: {
          label: 'Question',
          type: 'richtext',
          defaultValue: 'Are EV charging stations available?',
        },
        answer: {
          label: 'Answer',
          type: 'richtext',
          defaultValue:
            '<p>EV charging stations are not currently listed. Please contact the leasing office for updated information.</p>',
        },
      },
    },
    'faq.item6': {
      label: 'FAQ 6',
      fields: {
        question: {
          label: 'Question',
          type: 'richtext',
          defaultValue: 'What parking options are available?',
        },
        answer: {
          label: 'Answer',
          type: 'richtext',
          defaultValue:
            '<p>Reserved, covered, private garage, and guest parking are available. Contact the office for details on fees.</p>',
        },
      },
    },
    'faq.item7': {
      label: 'FAQ 7',
      fields: {
        question: {
          label: 'Question',
          type: 'richtext',
          defaultValue: 'What schools are located nearby?',
        },
        answer: {
          label: 'Answer',
          type: 'richtext',
          defaultValue:
            '<p><span data-type="field-token" data-field="property_name"></span> is located in Ahwatukee Foothills in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span>, near the Tempe Union High School District and Kyrene Elementary School District. Residents should confirm assignments with the schools directly.</p>',
        },
      },
    },
    'faq.item8': {
      label: 'FAQ 8',
      fields: {
        question: {
          label: 'Question',
          type: 'richtext',
          defaultValue: 'What are the major employers nearby?',
        },
        answer: {
          label: 'Answer',
          type: 'richtext',
          defaultValue:
            '<p>Enjoy easy freeway access to I-10, with proximity to employers in and around <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span> across the healthcare, technology, and finance sectors.</p>',
        },
      },
    },
    'faq.item9': {
      label: 'FAQ 9',
      fields: {
        question: {
          label: 'Question',
          type: 'richtext',
          defaultValue: 'What shopping, dining, and entertainment options are nearby?',
        },
        answer: {
          label: 'Answer',
          type: 'richtext',
          defaultValue:
            '<p>Residents enjoy access to South Mountain Park for outdoor recreation, Oasis Water Park, and Urban Air Trampoline Park, plus local restaurants and retail shops.</p>',
        },
      },
    },
    'faq.item10': {
      label: 'FAQ 10',
      fields: {
        question: {
          label: 'Question',
          type: 'richtext',
          defaultValue: 'What public transportation options are available near the community?',
        },
        answer: {
          label: 'Answer',
          type: 'richtext',
          defaultValue:
            '<p>The community is near major <span data-type="field-token" data-field="property_address_city"></span> roadways with freeway access. Valley Metro bus and light rail information is available on their website.</p>',
        },
      },
    },
    'faq.item11': {
      label: 'FAQ 11',
      fields: {
        question: {
          label: 'Question',
          type: 'richtext',
          defaultValue: 'What do I need to bring when applying or touring?',
        },
        answer: {
          label: 'Answer',
          type: 'richtext',
          defaultValue:
            '<p>A government-issued photo ID is required for tours. Contact the office for additional application documentation needs.</p>',
        },
      },
    },
    'faq.item12': {
      label: 'FAQ 12',
      fields: {
        question: {
          label: 'Question',
          type: 'richtext',
          defaultValue: 'What are the application and administrative fees?',
        },
        answer: {
          label: 'Answer',
          type: 'richtext',
          defaultValue:
            '<p>The application fee is $65 per applicant and the administrative fee is $300 per unit (non-refundable). A security deposit of $300 to one month’s rent is required at move-in.</p>',
        },
      },
    },
    'faq.item13': {
      label: 'FAQ 13',
      fields: {
        question: {
          label: 'Question',
          type: 'richtext',
          defaultValue: 'What lease terms are available?',
        },
        answer: {
          label: 'Answer',
          type: 'richtext',
          defaultValue:
            '<p>Lease terms of 9 to 12 months are available. Contact the office for current options.</p>',
        },
      },
    },
    'faq.item14': {
      label: 'FAQ 14',
      fields: {
        question: {
          label: 'Question',
          type: 'richtext',
          defaultValue: 'Is renter’s insurance required?',
        },
        answer: {
          label: 'Answer',
          type: 'richtext',
          defaultValue:
            '<p>Yes, renter’s insurance is required. Alternatively, a damage waiver of $16.50 per month is available instead.</p>',
        },
      },
    },
    'faq.item15': {
      label: 'FAQ 15',
      fields: {
        question: {
          label: 'Question',
          type: 'richtext',
          defaultValue: 'What is included in the monthly rent?',
        },
        answer: {
          label: 'Answer',
          type: 'richtext',
          defaultValue:
            '<p>Access to the apartment and community amenities is included. Utilities, parking upgrades, and pet rent vary. Contact the office for a specific breakdown.</p>',
        },
      },
    },
    'faq.item16': {
      label: 'FAQ 16',
      fields: {
        question: { label: 'Question', type: 'richtext', defaultValue: '' },
        answer: { label: 'Answer', type: 'richtext', defaultValue: '' },
      },
    },
    'faq.item17': {
      label: 'FAQ 17',
      fields: {
        question: { label: 'Question', type: 'richtext', defaultValue: '' },
        answer: { label: 'Answer', type: 'richtext', defaultValue: '' },
      },
    },
    'faq.item18': {
      label: 'FAQ 18',
      fields: {
        question: { label: 'Question', type: 'richtext', defaultValue: '' },
        answer: { label: 'Answer', type: 'richtext', defaultValue: '' },
      },
    },
    'faq.item19': {
      label: 'FAQ 19',
      fields: {
        question: { label: 'Question', type: 'richtext', defaultValue: '' },
        answer: { label: 'Answer', type: 'richtext', defaultValue: '' },
      },
    },
    'faq.item20': {
      label: 'FAQ 20',
      fields: {
        question: { label: 'Question', type: 'richtext', defaultValue: '' },
        answer: { label: 'Answer', type: 'richtext', defaultValue: '' },
      },
    },
  },
};

(FAQ as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FAQ;
